// src/utils/shareStore.js
// IndexedDB storage for tracking shared album links
// Enables users to view and deactivate their shared links
//
// ARCHITECTURE:
// - Separate IndexedDB database (doesn't interfere with photos/trips)
// - Stores metadata about each share (not the actual photos)
// - Tracks Firebase Storage paths for clean deletion
//
// USAGE:
// - saveShareRecord() - Called after successful upload
// - getAllShares() - For displaying list in UI
// - deleteShareRecord() - Called after Firebase deletion
// - getShareRecord() - For individual share lookup

const DB_NAME = 'VivaJourSharesDB';
const DB_VERSION = 1;
const STORE_NAME = 'shares';

let dbInstance = null;
let dbInitPromise = null;

/**
 * Initialize IndexedDB for shares
 * Uses singleton pattern to reuse connections
 */
function initShareDB() {
  if (dbInstance) {
    // Check if connection is still valid
    try {
      dbInstance.transaction(STORE_NAME, 'readonly');
      return Promise.resolve(dbInstance);
    } catch (e) {
      console.warn('[ShareStore] Connection closed, reconnecting...');
      dbInstance = null;
      dbInitPromise = null;
    }
  }
  
  if (dbInitPromise) return dbInitPromise;
  
  dbInitPromise = new Promise((resolve, reject) => {
    console.log('[ShareStore] Opening database...');
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('[ShareStore] Failed to open database:', request.error);
      dbInitPromise = null;
      reject(request.error);
    };
    
    request.onsuccess = () => {
      dbInstance = request.result;
      console.log('[ShareStore] ✅ Database opened successfully');
      
      // Handle connection closing
      dbInstance.onclose = () => {
        console.warn('[ShareStore] Database connection closed');
        dbInstance = null;
        dbInitPromise = null;
      };
      
      resolve(dbInstance);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      console.log('[ShareStore] Creating database schema...');
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'shareId' });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('tripId', 'tripId', { unique: false });
        store.createIndex('albumTitle', 'albumTitle', { unique: false });
        console.log('[ShareStore] Created shares store with indexes');
      }
    };
  });
  
  return dbInitPromise;
}

/**
 * Save a share record to IndexedDB
 * Called after successful Firebase upload
 * 
 * @param {Object} record - Share record containing:
 *   - shareId: string (Firebase share ID)
 *   - albumTitle: string (Album/trip name)
 *   - shareUrl: string (Full shareable URL)
 *   - createdAt: number (timestamp)
 *   - photoCount: number
 *   - tripId: string (optional - link to source trip)
 *   - storagePaths: string[] (Firebase Storage paths for deletion)
 * @returns {Promise<string>} Share ID
 */
export async function saveShareRecord(record) {
  try {
    const db = await initShareDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    // Ensure required fields
    const recordToSave = {
      ...record,
      createdAt: record.createdAt || Date.now(),
    };
    
    await new Promise((resolve, reject) => {
      const request = store.put(recordToSave);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    console.log('[ShareStore] ✅ Saved share record:', record.shareId);
    return record.shareId;
  } catch (error) {
    console.error('[ShareStore] Failed to save share record:', error);
    throw error;
  }
}

/**
 * Get all share records for display
 * Returns newest first
 * 
 * @returns {Promise<Array>} Array of share records
 */
export async function getAllShares() {
  try {
    const db = await initShareDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const shares = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    // Sort by most recently created
    return shares.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  } catch (error) {
    console.error('[ShareStore] Failed to get shares:', error);
    return [];
  }
}

/**
 * Get a single share record by ID
 * 
 * @param {string} shareId - Share ID
 * @returns {Promise<Object|null>} Share record or null
 */
export async function getShareRecord(shareId) {
  try {
    const db = await initShareDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const record = await new Promise((resolve, reject) => {
      const request = store.get(shareId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return record || null;
  } catch (error) {
    console.error('[ShareStore] Failed to get share record:', error);
    return null;
  }
}

/**
 * Delete a share record from IndexedDB
 * Called after Firebase deletion is complete
 * 
 * @param {string} shareId - Share ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteShareRecord(shareId) {
  try {
    const db = await initShareDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    await new Promise((resolve, reject) => {
      const request = store.delete(shareId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    console.log('[ShareStore] ✅ Deleted share record:', shareId);
    return true;
  } catch (error) {
    console.error('[ShareStore] Failed to delete share record:', error);
    return false;
  }
}

/**
 * Get total share count
 * 
 * @returns {Promise<number>} Number of shares
 */
export async function getShareCount() {
  try {
    const db = await initShareDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const count = await new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return count;
  } catch (error) {
    console.error('[ShareStore] Failed to get share count:', error);
    return 0;
  }
}

/**
 * Get shares by trip ID
 * Useful for showing which shares exist for a specific trip
 * 
 * @param {string} tripId - Trip ID to filter by
 * @returns {Promise<Array>} Array of share records for this trip
 */
export async function getSharesByTripId(tripId) {
  try {
    const db = await initShareDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('tripId');
    
    const shares = await new Promise((resolve, reject) => {
      const request = index.getAll(tripId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return shares.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  } catch (error) {
    console.error('[ShareStore] Failed to get shares by trip:', error);
    return [];
  }
}

/**
 * Update a share record (e.g., to update title)
 * 
 * @param {string} shareId - Share ID to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<boolean>} Success status
 */
export async function updateShareRecord(shareId, updates) {
  try {
    const existing = await getShareRecord(shareId);
    if (!existing) {
      console.warn('[ShareStore] Share not found for update:', shareId);
      return false;
    }
    
    const updated = {
      ...existing,
      ...updates,
      shareId, // Ensure shareId isn't overwritten
      updatedAt: Date.now(),
    };
    
    await saveShareRecord(updated);
    return true;
  } catch (error) {
    console.error('[ShareStore] Failed to update share record:', error);
    return false;
  }
}

/**
 * Clear all share records
 * USE WITH CAUTION - for testing/reset only
 * 
 * @returns {Promise<boolean>} Success status
 */
export async function clearAllShares() {
  try {
    const db = await initShareDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    await new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    console.log('[ShareStore] ✅ Cleared all share records');
    return true;
  } catch (error) {
    console.error('[ShareStore] Failed to clear shares:', error);
    return false;
  }
}

// Export for use in share-v2.js and SharedLinksManager
export default {
  saveShareRecord,
  getAllShares,
  getShareRecord,
  deleteShareRecord,
  getShareCount,
  getSharesByTripId,
  updateShareRecord,
  clearAllShares,
};
